import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  CommonAreaType,
  ParkingSpotType,
  RoomType,
  SpaceDiscriminator,
  UpdateSpacePayload,
  usePutCommonAreasByIdMutation,
  usePutParkingSpotsByIdMutation,
  usePutRoomsByIdMutation,
  usePutSpacesByIdMutation,
} from '@client/api';

import { SpaceForm, SpaceFormData } from '../../components/space.form';
import { useGetGenericSpace } from '../../hooks/use-get-generic-space';

interface SpaceEditModalProps {
  open: boolean;
  onClose: () => void;
  onAfterSubmit: () => void;
}

export const SpaceEditModal = ({ open, onClose, onAfterSubmit }: SpaceEditModalProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { spaceId, discriminator } = useParams<{ spaceId: string; discriminator: SpaceDiscriminator }>();

  const {
    data: space,
    isFetching,
    isError,
    isCommonArea,
    isRoom,
    isParkingSpot,
  } = useGetGenericSpace(spaceId, discriminator);

  const [mutation] = usePutSpacesByIdMutation();
  const [commonAreaMutation] = usePutCommonAreasByIdMutation();
  const [roomMutation] = usePutRoomsByIdMutation();
  const [parkingSpotMutation] = usePutParkingSpotsByIdMutation();

  const handleSubmit = async (data: SpaceFormData) => {
    if (spaceId == null) return;

    const payload: UpdateSpacePayload = {
      parentId: data.parent?.id,
      name: data.name,
      description: data.description,
      areaMetric: data.areaMetric,
      source: space?.source,
      rentPriceId: data.rentPriceId,
      assetClass: data.assetClass || undefined,
    };

    try {
      switch (discriminator) {
        case 'CommonArea':
          await commonAreaMutation({
            id: spaceId,
            body: {
              ...payload,
              operationalCostPriceId: data.operationalCostPriceId,
              type: data.commonAreaType as CommonAreaType,
              parentId: data.parent?.id as string,
            },
          }).unwrap();
          break;
        case 'Room':
          await roomMutation({
            id: spaceId,
            body: {
              ...payload,
              type: data.roomType as RoomType,
              parentId: data.parent?.id as string,
            },
          }).unwrap();
          break;
        case 'ParkingSpot':
          await parkingSpotMutation({
            id: spaceId,
            body: {
              ...payload,
              type: data.parkingSpotType as ParkingSpotType,
              parentId: data.parent?.id as string,
            },
          }).unwrap();
          break;
        default:
          await mutation({
            id: spaceId,
            body: payload,
          }).unwrap();
      }
    } catch (e) {
      return;
    }

    onAfterSubmit();
  };

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (!isError) {
      return;
    }
    onClose();
  }, [onClose, isError]);

  return (
    <SpaceForm
      open={open}
      title={tCommon('editEntity', {
        entity: discriminator ? tType(`SpaceDiscriminator.${discriminator}`).toLowerCase() : tGlobal('entity.space'),
      })}
      onSubmit={handleSubmit}
      onClose={handleClose}
      isFetching={isFetching}
      defaultValues={{
        discriminator: space?.discriminator || discriminator,
        name: space?.name,
        description: space?.description,
        areaMetric: space?.area?.metric,
        rentPriceId: space?.rent?.id,
        parent: space?.parent,
        assetClass: space?.assetClass,
        operationalCostPriceId: isCommonArea(space) ? space?.operationalCost?.id : undefined,
        commonAreaType: isCommonArea(space) ? space?.type : undefined,
        roomType: isRoom(space) ? space?.type : undefined,
        parkingSpotType: isParkingSpot(space) ? space?.type : undefined,
      }}
    />
  );
};
